import { useEffect, useRef, useState } from "react";

import { CoreService } from "@/services/core/CoreService";
import {
  AnalyticsServiceV2,
  type ChangingRoomSuspectItem,
  type ChangingRoomSuspectItemSourceType,
} from "@/services/features/AnalyticsServiceV2";
import InventoryService, {
  type InventoryItem,
} from "@/services/features/inventory/InventoryService";
import { type Store, StoreService } from "@/services/features/StoreService";
import { DateRange, storePerformanceService } from "@/services/StorePerformanceService";
import { formatStoreDate, formatStoreDateKey, getBrowserTimezone } from "@/utils/storeTime";

export interface ContinuousReadOccurrence {
  id: number;
  time: string;
  storeId: number;
  imageUrl: string;
  productName: string;
  sku: string;
  size: string;
  color: string;
  storeName: string;
  storeNumber: string;
  searchTimeSeconds: number;
  value?: number;
  createdAt: number;
  category: string;
  epc: string;
  roomId: number;
  roomLabel: string;
  roomNumber: string;
  sourceType: ChangingRoomSuspectItemSourceType;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface ContinuousReadsDaySummary {
  dateKey: string;
  label: string;
  count: number;
  totalValue: number;
}

export interface ContinuousReadsReportData {
  occurrences: ContinuousReadOccurrence[];
  daySummary: ContinuousReadsDaySummary[];
  totalReads: number;
  totalValue: number;
  dailyAverage: number;
  averageDailyValue: number;
}

const emptyReport: ContinuousReadsReportData = {
  occurrences: [],
  daySummary: [],
  totalReads: 0,
  totalValue: 0,
  dailyAverage: 0,
  averageDailyValue: 0,
};

const DAY_MS = 24 * 60 * 60 * 1000;
const LOCAL_DATE_LABEL_TIMEZONE = "Etc/UTC";

const toTimestamp = (value?: Date | string | number | null): number | null => {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? null : timestamp;
};

const toIsoString = (value: Date | string | number): string => {
  const timestamp = toTimestamp(value);
  return timestamp === null ? "" : new Date(timestamp).toISOString();
};

const parsePrice = (sku?: InventoryItem | null): number | undefined => {
  const rawPrice = sku?.salePrice || sku?.price;
  if (rawPrice == null) {
    return undefined;
  }

  const numericPrice = Number(rawPrice);
  return Number.isFinite(numericPrice) ? numericPrice : undefined;
};

const formatRoomNumber = (roomId: number): string => `FR-${String(roomId).padStart(2, "0")}`;

const formatRoomLabel = (roomId: number, roomName?: string): string => {
  const safeRoomName = roomName?.trim();
  return safeRoomName || formatRoomNumber(roomId);
};

const buildRoomKey = (storeId: number, roomId: number): string => `${storeId}:${roomId}`;

const getDateKeyRange = (startDate: string, endDate: string): string[] => {
  const parseDateKey = (value: string) => {
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) {
      return null;
    }

    return Date.UTC(year, month - 1, day);
  };

  const startTime = parseDateKey(startDate);
  const endTime = parseDateKey(endDate);

  if (startTime === null || endTime === null || endTime < startTime) {
    return [];
  }

  const keys: string[] = [];
  for (let time = startTime; time <= endTime; time += DAY_MS) {
    keys.push(new Date(time).toISOString().slice(0, 10));
  }

  return keys;
};

const getDayLabel = (dateKey: string, locale: string, rangeLength: number): string => {
  const date = new Date(`${dateKey}T12:00:00Z`);
  const options: Intl.DateTimeFormatOptions =
    rangeLength <= 7 ? { weekday: "short" } : { month: "short", day: "numeric" };

  return formatStoreDate(date, LOCAL_DATE_LABEL_TIMEZONE, locale, options);
};

const normalizeSuspectItems = (
  data: ChangingRoomSuspectItem[] | ChangingRoomSuspectItem | null | undefined,
): ChangingRoomSuspectItem[] => {
  if (!data) {
    return [];
  }

  return Array.isArray(data) ? data : [data];
};

const fetchRoomNames = async (
  stores: Store[],
  rows: ChangingRoomSuspectItem[],
): Promise<Map<string, string>> => {
  const storeService = StoreService.getInstance();
  const storeIdsWithRows = new Set(rows.map((row) => row.storeId));
  const roomNames = new Map<string, string>();

  for (const store of stores) {
    if (!storeIdsWithRows.has(store.id)) {
      continue;
    }

    const response = await storeService.getChangingRoomsByStore(store.id);
    if (!response.success || !response.data) {
      continue;
    }

    response.data.forEach((group) => {
      group.changingRooms.forEach((room) => {
        if (room.id !== undefined) {
          roomNames.set(buildRoomKey(store.id, room.id), room.name);
        }
      });
    });
  }

  return roomNames;
};

const findSkuForSuspectItem = async (
  inventoryService: InventoryService | null,
  row: ChangingRoomSuspectItem,
  skuCache: Map<string, InventoryItem | null>,
): Promise<InventoryItem | null> => {
  if (!inventoryService) {
    return null;
  }

  if (row.epc) {
    const epcCacheKey = `${row.storeId}:epc:${row.epc}`;
    if (!skuCache.has(epcCacheKey)) {
      try {
        skuCache.set(epcCacheKey, await inventoryService.parseEpc(row.storeId, row.epc));
      } catch {
        skuCache.set(epcCacheKey, null);
      }
    }

    const epcSku = skuCache.get(epcCacheKey);
    if (epcSku) {
      return epcSku;
    }
  }

  const skuId = row.skuId || null;
  if (!skuId) {
    return null;
  }

  const skuCacheKey = `${row.storeId}:sku:${skuId}`;
  if (!skuCache.has(skuCacheKey)) {
    try {
      skuCache.set(skuCacheKey, await inventoryService.getSkuBySkuId(row.storeId, skuId));
    } catch {
      skuCache.set(skuCacheKey, null);
    }
  }

  return skuCache.get(skuCacheKey) ?? null;
};

const buildReportData = (
  occurrences: ContinuousReadOccurrence[],
  startDate: string,
  endDate: string,
  storeTimezones: Map<number, string>,
  fallbackTimezone: string,
  locale: string,
): ContinuousReadsReportData => {
  const dateKeys = getDateKeyRange(startDate, endDate);
  const dateCount = Math.max(dateKeys.length, 1);
  const daySummaryByKey = new Map<string, { count: number; totalValue: number }>();

  dateKeys.forEach((dateKey) => {
    daySummaryByKey.set(dateKey, { count: 0, totalValue: 0 });
  });

  occurrences.forEach((row) => {
    const value = row.value ?? 0;
    const rowTimezone = storeTimezones.get(row.storeId) || fallbackTimezone;
    const dateKey = formatStoreDateKey(row.time, rowTimezone);
    const currentDay = daySummaryByKey.get(dateKey) ?? { count: 0, totalValue: 0 };
    currentDay.count += 1;
    currentDay.totalValue += value;
    daySummaryByKey.set(dateKey, currentDay);
  });

  const daySummary = Array.from(daySummaryByKey.entries()).map(([dateKey, stats]) => ({
    dateKey,
    label: getDayLabel(dateKey, locale, dateKeys.length),
    count: stats.count,
    totalValue: stats.totalValue,
  }));
  const totalReads = occurrences.length;
  const totalValue = occurrences.reduce((sum, row) => sum + (row.value ?? 0), 0);

  return {
    occurrences,
    daySummary,
    totalReads,
    totalValue,
    dailyAverage: totalReads / dateCount,
    averageDailyValue: totalValue / dateCount,
  };
};

export const useContinuousReadsReport = (
  stores: Store[] | null,
  dateRange?: DateRange,
  sourceType: ChangingRoomSuspectItemSourceType = "CHANGING_ROOM",
  locale = "en-US",
) => {
  const browserTimezone = getBrowserTimezone();
  const [data, setData] = useState<ContinuousReadsReportData>(emptyReport);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completedRequestKey, setCompletedRequestKey] = useState<string | null>(null);
  const activeRequestIdRef = useRef(0);
  const storeIdsKey = stores?.map((store) => store.id).join(",") ?? "";
  const startDate = storePerformanceService.formatDate(dateRange?.start, browserTimezone);
  const endDate = storePerformanceService.formatDate(dateRange?.end, browserTimezone);
  const hasRequestInputs = Boolean(storeIdsKey && startDate && endDate);
  const requestKey = hasRequestInputs
    ? `${storeIdsKey}:${startDate}:${endDate}:${sourceType}:${locale}`
    : null;

  useEffect(() => {
    activeRequestIdRef.current += 1;

    if (!stores || stores.length === 0 || !dateRange) {
      setData(emptyReport);
      setLoading(false);
      setTableLoading(false);
      setError(null);
      setCompletedRequestKey(null);
      return;
    }

    const requestId = activeRequestIdRef.current;
    let cancelled = false;

    const isActive = () => !cancelled && activeRequestIdRef.current === requestId;

    const fetchData = async () => {
      setLoading(true);
      setTableLoading(true);
      setError(null);
      setData(emptyReport);

      try {
        const startDate = storePerformanceService.formatDate(dateRange.start, browserTimezone);
        const endDate = storePerformanceService.formatDate(dateRange.end, browserTimezone);
        const analyticsService = AnalyticsServiceV2.getInstance();
        const response = await analyticsService.getContinuousReadsReport({
          storeIds: stores.map((store) => store.id),
          startDate,
          endDate,
          sourceType,
        });
        const rows = normalizeSuspectItems(response.data);
        const storeMap = new Map(stores.map((store) => [store.id, store]));
        const storeTimezones = new Map(
          stores.map((store) => [store.id, store.timezone || browserTimezone]),
        );
        const roomNames = await fetchRoomNames(stores, rows);
        const token = CoreService.getInstance().getAuthorizationHeader();
        const inventoryService = token ? new InventoryService(token) : null;
        const skuCache = new Map<string, InventoryItem | null>();
        const occurrences: ContinuousReadOccurrence[] = [];

        for (const row of rows) {
          if (!isActive()) {
            return;
          }

          const timestamp =
            toTimestamp(row.lastSeenAt) ??
            toTimestamp(row.createdAt) ??
            toTimestamp(row.firstSeenAt);
          if (timestamp === null) {
            continue;
          }

          const sku = await findSkuForSuspectItem(inventoryService, row, skuCache);
          const store = storeMap.get(row.storeId);
          const roomName = roomNames.get(buildRoomKey(row.storeId, row.changingRoomId));
          const value = parsePrice(sku);

          occurrences.push({
            id: row.id,
            time: new Date(timestamp).toISOString(),
            storeId: row.storeId,
            imageUrl: sku?.imageUrl || "",
            productName: sku?.title || "",
            sku: sku?.skuId || row.skuId || "--",
            size: sku?.size || "--",
            color: sku?.color || "--",
            storeName: store?.name || "--",
            storeNumber: store?.externalId || String(row.storeId),
            searchTimeSeconds: 0,
            value,
            createdAt: timestamp,
            category: sku?.category || "--",
            epc: row.epc,
            roomId: row.changingRoomId,
            roomLabel: formatRoomLabel(row.changingRoomId, roomName),
            roomNumber: formatRoomNumber(row.changingRoomId),
            sourceType: row.sourceType,
            firstSeenAt: toIsoString(row.firstSeenAt),
            lastSeenAt: toIsoString(row.lastSeenAt),
          });
        }

        if (!isActive()) {
          return;
        }

        occurrences.sort((a, b) => b.createdAt - a.createdAt);
        setData(
          buildReportData(occurrences, startDate, endDate, storeTimezones, browserTimezone, locale),
        );
      } catch (err) {
        if (!isActive()) {
          return;
        }

        setError(err instanceof Error ? err.message : "Failed to load continuous reads report");
        setData(emptyReport);
      } finally {
        if (isActive()) {
          setLoading(false);
          setTableLoading(false);
          setCompletedRequestKey(requestKey);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [stores, dateRange, sourceType, locale, browserTimezone, requestKey]);

  const isRequestPending = hasRequestInputs && completedRequestKey !== requestKey;

  return {
    data,
    loading: loading || isRequestPending,
    tableLoading: tableLoading || isRequestPending,
    error,
  };
};
